/**
 * <biosphere-subscribe-card> — enhancement for the subscribe option rendered by
 * snippets/biosphere-subscribe-card.liquid.
 *
 * It owns exactly one thing: whether the plan select is submitted. The card's radio
 * shares a name with the volume tiers, so the browser already guarantees one pick
 * across all four options — this only translates that into "post a selling_plan"
 * or "post none".
 *
 * The select is never disabled. A disabled control cannot be opened, and a buyer
 * reading the frequencies is a buyer deciding whether to subscribe. Instead the
 * field is kept out of the submission by dropping its `name`: a named control
 * posts, an unnamed one does not, and posting no `selling_plan` is what a one-time
 * purchase means. Touching the frequency therefore also picks the subscription.
 *
 * When it is picked, it owns the price. The volume tiers and this card are
 * alternatives, and the tiers write into the same slots. Each plan's price was
 * formatted by Liquid onto its own option; this copies one across and says so, and
 * the tier control takes the slots back when a tier is picked again.
 */
class BiosphereSubscribeCard extends HTMLElement {
  #radio;
  #choice;
  #select;
  #price;
  #pack;
  #quotes;
  #subscribing = false;

  // The quantity this card owns. A tier is a one-time quantity and a subscription is
  // a standing order — two different numbers sharing one field on the form — so the
  // card holds its own and puts it back whenever it is picked.
  #own = 1;
  #abort;

  connectedCallback() {
    this.#radio = this.querySelector(".biosphere-subscribe-card__input");
    this.#choice = this.querySelector(".biosphere-subscribe-card__choice");
    this.#select = this.querySelector("[data-selling-plan-select]");
    this.#price = this.querySelector("[data-subscribe-price]");
    this.#pack = this.querySelector("[data-subscribe-pack]");
    this.#own = Number(this.#quantityInput()?.min) || 1;

    // What each plan costs and saves at each quantity, keyed by both. Server-formatted,
    // like every other figure in the buy box.
    this.#quotes = Object.fromEntries(
      [...this.querySelectorAll("[data-sub-quote]")].map((quote) => [
        `${quote.dataset.plan}:${quote.dataset.quantity}`,
        quote.dataset,
      ]),
    );
    // Only capture a real name. #apply() strips it while one-time is chosen, so a
    // second connect would otherwise record "" and the field could never post again.
    if (this.#select?.name) {
      this.#select.dataset.sellingPlanName = this.#select.name;
    }

    // Without the plan select there is nothing to gate, so the baseline stands: the
    // card stays as rendered and the select, if any, keeps its one-time option.
    if (!this.#radio || !this.#choice || !this.#select) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.addEventListener(
      "change",
      (event) => {
        if (event.target?.name !== this.#radio.name) return;
        this.#apply();
      },
      { signal },
    );

    // Choosing a frequency is choosing to subscribe — and, once subscribing, is
    // changing what is being committed to, so the price and button follow either way.
    this.#select.addEventListener(
      "change",
      () => {
        if (this.#radio.checked) {
          this.#apply();
          return;
        }
        this.#radio.checked = true;
        this.#radio.dispatchEvent(new Event("change", { bubbles: true }));
      },
      { signal },
    );

    // A variant change re-renders parts of the page from the server, and the server
    // does not know which purchase option is picked — it renders the resting state. So
    // anything rebuilt comes back saying "Add to cart" underneath a chosen
    // subscription, and the selection is written over it again.
    //
    // On the next frame, because the components that rebuild themselves listen to this
    // same event: running inline would write the state and then have it replaced.
    document.addEventListener(
      "biosphere:variant-picker:update",
      () => requestAnimationFrame(() => this.#apply()),
      { signal },
    );

    // While this card holds the selection the quantity is its quantity, wherever it was
    // moved from — the buy box stepper, the sticky bar, the keyboard. Remembered so
    // returning to the card returns to it.
    document.addEventListener(
      "biosphere:quantity:change",
      () => {
        // The radio, not the cached flag: picking a tier unchecks this card
        // and moves the quantity in one gesture, and listener order decides
        // which this hears first.
        if (!this.#radio.checked) return;
        this.#own = Number(this.#quantityInput()?.value) || this.#own;

        // Not just the pack line: two packs a delivery cost twice as much and save twice as
        // much, and both are stated on this page.
        this.#apply();
      },
      { signal },
    );

    // The card becomes the radio for the keyboard, for the reason set out in
    // biosphere-buy-tiers.js: a radio whose group has a checked member is not reachable
    // by Tab, and tabindex will not change that. The real input stays underneath as the
    // form control.
    // A generic element supports the radio role; a label does not.
    // Keep the existing input and children so form and styling hooks are unchanged.
    if (this.#choice.tagName === "LABEL") {
      const control = document.createElement("div");
      for (const attribute of this.#choice.attributes) {
        control.setAttribute(attribute.name, attribute.value);
      }
      while (this.#choice.firstChild) control.append(this.#choice.firstChild);
      this.#choice.replaceWith(control);
      this.#choice = control;
    }
    this.#choice.addEventListener(
      "click",
      (event) => {
        if (event.target === this.#radio) return;
        this.#choice.focus();
        this.#radio.click();
      },
      { signal },
    );
    this.#choice.setAttribute("role", "radio");
    this.#choice.setAttribute("aria-label", this.#choice.textContent.trim());
    this.#choice.tabIndex = 0;
    this.#radio.setAttribute("aria-hidden", "true");
    this.#radio.tabIndex = -1;

    this.#choice.addEventListener(
      "keydown",
      (event) => {
        if (event.key !== " " && event.key !== "Enter") return;
        event.preventDefault();
        if (this.#radio.checked) return;
        this.#radio.checked = true;
        this.#radio.dispatchEvent(new Event("change", { bubbles: true }));
      },
      { signal },
    );

    // Back/forward restores the radio after connectedCallback and without
    // firing change, leaving the card picked but the select unnamed.
    window.addEventListener("pageshow", () => this.#resync(), { signal });

    document
      .getElementById(this.dataset.formId)
      ?.addEventListener("submit", () => this.#armAppstle(), {
        signal,
        capture: true,
      });

    this.#apply();
    this.#choice.hidden = false;
  }

  /**
   * Appstle stamps its widget's selling_plan onto every /cart/add. This is the
   * element it reads — armed for this form's submit only, or it hijacks the
   * drawer's pair add too. Cleared once the request carrying it exists.
   */
  #armAppstle() {
    const appstle = this.querySelector("[data-appstle-shim]");
    if (!appstle) return;

    appstle.checked = this.#radio.checked;
    appstle.value = this.#radio.checked ? (this.#select.value ?? "") : "";

    setTimeout(() => {
      appstle.checked = false;
      appstle.value = "";
    }, 0);
  }

  /** Adopt what the browser restored instead of treating it as a fresh pick. */
  #resync() {
    // Priming #subscribing stops #apply() seeing a transition and resetting
    // the quantity the buyer left.
    if (this.#radio.checked) {
      this.#own = Number(this.#quantityInput()?.value) || this.#own;
    }
    this.#subscribing = this.#radio.checked;
    this.#apply();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** The one quantity the form posts, wherever it is driven from. */
  #quantityInput() {
    return document
      .getElementById(this.dataset.formId)
      ?.querySelector('input[name="quantity"]');
  }

  /** Put this card's own quantity back into the form. */
  #restoreQuantity() {
    const input = this.#quantityInput();
    if (!input || Number(input.value) === this.#own) return;

    input.value = String(this.#own);
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /**
   * What arrives each delivery, stated in the sentence that promises it. Both nouns
   * and the sentence came from Liquid already translated; this only chooses between
   * them and fills the slots.
   */
  #paintPack() {
    if (!this.#pack) return;

    const noun =
      this.#own === 1 ? this.dataset.packOne : this.dataset.packOther;

    this.#pack.textContent = this.dataset.packTemplate
      .replace("[count]", this.#own)
      .replace("[noun]", noun);
  }

  #apply() {
    const subscribing = this.#radio.checked;

    // The moment of choosing, not the state of being chosen: the reset below belongs to
    // the transition. Re-running it on every apply would undo the buyer's own stepper
    // the instant they touched it.
    const chosen = subscribing && !this.#subscribing;
    this.#subscribing = subscribing;

    // Named = submitted. The name is restored from the attribute Liquid wrote rather
    // than hardcoded, so the field this posts stays the ported component's business.
    const wanted = subscribing ? this.#select.dataset.sellingPlanName : null;
    if (this.#select.getAttribute("name") !== wanted) {
      if (wanted) this.#select.name = wanted;
      else this.#select.removeAttribute("name");
    }

    this.classList.toggle("biosphere-subscribe-card--selected", subscribing);
    this.#choice.setAttribute("aria-checked", String(subscribing));

    const option = this.#select.selectedOptions?.[0];
    const scope = CSS.escape(this.dataset.priceScope ?? "");

    // The order's figure, not one pack's. Past the end of the table the plan's own
    // per-delivery price stands — true of a single pack, and the honest answer where
    // nothing has priced the rest.
    const quote = this.#quotes?.[`${option?.value}:${this.#own}`];
    const price = quote?.price ?? option?.dataset.planPrice;

    if (subscribing) {
      const saving = document.querySelector(`[data-tier-saving="${scope}"]`);
      if (saving) {
        const slot = saving.querySelector("[data-tier-text]") ?? saving;
        slot.textContent = quote?.saving ?? "";
        saving.hidden = !quote?.saving;
      }
    }

    // The "[saving]" benefit line follows the plan and quantity, like the price,
    // and hides for a plan that saves nothing.
    const savingSlot = this.querySelector("[data-subscribe-saving]");
    if (savingSlot && quote) {
      if (quote.savingAmount) savingSlot.textContent = quote.savingAmount;
      savingSlot.closest("li").hidden = !quote.savingAmount;
    }

    if (subscribing && price) {
      if (this.#price) this.#price.textContent = price;

      document
        .querySelectorAll(`[data-tier-price="${scope}"]`)
        .forEach((slot) => {
          slot.textContent = price;
        });
    }

    // The button's verb, because a button saying "Add to cart" under a chosen
    // subscription describes the wrong transaction. It also drops the schedule in
    // beside its price, so the commitment is stated on the control that makes it.
    //
    // Looked up on each apply, never cached: the variant picker re-renders the form,
    // and a reference taken at connect would point at a button no longer on the page.
    document
      .querySelectorAll(`[data-tier-label="${scope}"]`)
      .forEach((slot) => {
        slot.textContent = subscribing
          ? this.dataset.ctaLabel
          : slot.dataset.labelDefault;

        // The schedule goes beside the price on the same button, so the commitment is
        // stated on the control that makes it.
        const priceSlot = slot.parentElement?.querySelector(
          `[data-tier-price="${scope}"]`,
        );
        if (subscribing && price && priceSlot && option?.dataset.planName) {
          priceSlot.textContent = this.dataset.ctaPriceTemplate
            .replace("[price]", price)
            .replace("[plan]", option.dataset.planName);
        }
      });

    // The shipping promise changes with the purchase. A one-time order ships free once
    // it clears the threshold; a subscription ships free every time, which is a
    // different and better promise and the reason the card lists it as a benefit.
    //
    // The tier control owns this line otherwise, and takes it back on the next tier
    // change — the same handover as the price above.
    if (subscribing) {
      const shipping = document.querySelector(
        `[data-tier-shipping="${scope}"]`,
      );
      if (shipping && this.dataset.shipsFree) {
        const slot = shipping.querySelector("[data-tier-text]") ?? shipping;
        slot.textContent = this.dataset.shipsFree;
        shipping.hidden = false;
        shipping.classList.add("biosphere-product-hero__nudge--free");
      }
    }

    // Carrying 3 Tubs into a subscription would sign the buyer up for three every
    // delivery, which is not what anyone choosing "the easy way to never run out" is
    // asking for. So the card puts its own quantity back: one the first time, and
    // afterwards whatever it was last set to while subscribed.
    if (chosen) this.#restoreQuantity();
    this.#paintPack();

    // Announced rather than written anywhere else — what the sticky bar does with a
    // subscription is not this card's business.
    this.dispatchEvent(
      new CustomEvent("biosphere:subscribe-card:change", {
        bubbles: true,
        detail: {
          scope: this.dataset.priceScope,
          subscribing,
          plan: option?.dataset.planName,
          price,
        },
      }),
    );
  }
}

if (!customElements.get("biosphere-subscribe-card")) {
  customElements.define("biosphere-subscribe-card", BiosphereSubscribeCard);
}
